import type { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import { useInfiniteQuery } from '@tanstack/react-query';

import type {
    AiTransformationItemsResponse,
    AiTransformationSubject,
} from '../../../data-contract/ai-transformation-strategy';
import type { AiTransformationFilters } from '../../../fetchers/position-management/ai-transformation-fetchers';
import { unwrapResult } from '../../../fetchers/unwrap-result';
import { useApplicationFetchers } from '../../../fetchers/use-application-fetchers';
import { POSITION_DERIVED_QUERY_META } from '../../../position-management';
import { useLocale } from '../../components/use-locale';
import { strategyKeys } from './query-keys';
import { nextItemsPage } from './use-strategy-status-column';

const GROUP_PAGE_SIZE = 50;

export type StrategyGroupGrouping = 'LEVEL' | 'ROLE';

/**
 * The positions, or the tasks, of one role or level under the given filters, a page at a time. The page's own role or
 * level filter still applies: a group it leaves out lists nothing, without asking the backend.
 */
export const useStrategyGroupItems = ({
    architectureId,
    filters,
    groupId,
    grouping,
    subject = 'POSITION',
}: {
    readonly architectureId: string;
    readonly filters: AiTransformationFilters;
    readonly groupId: string;
    readonly grouping: StrategyGroupGrouping;
    readonly subject?: AiTransformationSubject;
}): UseInfiniteQueryResult<InfiniteData<AiTransformationItemsResponse, number>, Error> => {
    const { fetchAiTransformationItems } = useApplicationFetchers();
    const acceptLanguage = useLocale();
    const pageGroupIds = grouping === 'ROLE' ? filters.roleId : filters.levelId;
    const isFilteredOut = pageGroupIds.length > 0 && !pageGroupIds.includes(groupId);
    const groupFilters: AiTransformationFilters =
        grouping === 'ROLE' ? { ...filters, roleId: [groupId] } : { ...filters, levelId: [groupId] };

    return useInfiniteQuery({
        getNextPageParam: nextItemsPage,
        initialPageParam: 0,
        queryFn: ({ pageParam, signal }) =>
            isFilteredOut
                ? Promise.resolve({ items: [], page: 0, size: GROUP_PAGE_SIZE, total: 0 })
                : unwrapResult(
                      fetchAiTransformationItems({
                          acceptLanguage,
                          architectureId,
                          filters: groupFilters,
                          page: pageParam,
                          signal,
                          size: GROUP_PAGE_SIZE,
                          subject,
                      }),
                  ),
        meta: POSITION_DERIVED_QUERY_META,
        queryKey: strategyKeys.groupItems(architectureId, { filters, groupId, grouping, subject }, acceptLanguage),
        throwOnError: false,
    });
};
